import { type QueryClient } from "@tanstack/react-query";
import { redirect, redirectDocument } from "react-router";

import { getMeQueryOptions } from "iqf-web-ui/security/me";

import { type UdzsAuthClient } from "./auth-client-types";
import {
  isAuthFlowRestartRequiredError,
  isUnauthorizedError,
} from "./auth-errors";

export type RequireMeParams = {
  request: Request;
  authClient: UdzsAuthClient;
  queryClient: QueryClient;
  meApi: string;
};

export async function requireMe({
  request,
  authClient,
  queryClient,
  meApi,
}: RequireMeParams) {
  const url = new URL(request.url);

  if (authClient.isOAuthFlowPath(url.pathname)) {
    return null;
  }

  if (!authClient.hasStoredAuth()) {
    authClient.storeRedirectUrl(url);
    throw redirectDocument(await authClient.getLoginUrl());
  }

  try {
    await authClient.ensureValidAccessToken();
    authClient.applyAxiosAuthorization();

    const me = await queryClient.ensureQueryData(
      getMeQueryOptions({ api: meApi }),
    );

    if (!me) {
      authClient.storeRedirectUrl(url);
      throw redirectDocument(await authClient.getLoginUrl());
    }

    return me;
  } catch (error) {
    if (isUnauthorizedError(error)) {
      authClient.clearLocalAuth();
      authClient.storeRedirectUrl(url);
      throw redirectDocument(await authClient.getLoginUrl());
    }

    throw error;
  }
}

export type OAuthRedirectParams = {
  request: Request;
  authClient: UdzsAuthClient;
  queryClient: QueryClient;
  meApi: string;
};

export async function handleOAuthRedirect({
  request,
  authClient,
  queryClient,
  meApi,
}: OAuthRedirectParams) {
  try {
    await authClient.handleLoginRedirect(request.url);
  } catch (error) {
    if (!isAuthFlowRestartRequiredError(error)) {
      throw error;
    }

    authClient.clearLocalAuth();
    queryClient.clear();

    throw redirectDocument(await authClient.getRecoveryLoginUrl());
  }

  authClient.applyAxiosAuthorization();

  await queryClient.ensureQueryData(getMeQueryOptions({ api: meApi }));

  throw redirect(authClient.consumeRedirectUrl() || "/");
}

export type OAuthLogoutParams = {
  authClient: UdzsAuthClient;
  queryClient: QueryClient;
};

export function handleOAuthLogout({
  authClient,
  queryClient,
}: OAuthLogoutParams) {
  authClient.handleLogoutRedirect();
  queryClient.clear();

  throw redirect("/");
}
